"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Button } from "@/components/ui/button"
import {
  Menu,
  X,
  User,
  Building2,
  LayoutDashboard,
  LogOut,
  Settings,
  Briefcase,
  Users,
  FileText,
  Home,
  Search,
  Globe,
  DollarSign,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuth } from "@/hooks/use-auth"
import { useRouter } from "next/navigation"
import { toast } from "sonner"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useTranslation } from "react-i18next"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const pathname = usePathname()
  const { user, company, isCompany, signOut, loading } = useAuth()
  const router = useRouter()
  const { t, i18n } = useTranslation()

  const isActive = (path: string) => pathname === path
  const isRTL = i18n.language === "ar"

  useEffect(() => {
    setMounted(true)

    const handleScroll = () => {
      const scrollY = window.scrollY
      // Consider scrolled if we've scrolled past the hero section (roughly viewport height)
      setIsScrolled(scrollY > window.innerHeight * 0.8)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // Check initial state

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const toggleMenu = () => setIsOpen(!isOpen)

  const handleLogout = async () => {
    try {
      await signOut()
      toast.success("Signed out successfully")
      router.push("/")
      setIsOpen(false)
    } catch (error) {
      console.error("Logout error:", error)
      toast.error("Failed to sign out")
    }
  }

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng)
  }

  const isHomePage = pathname === "/"

  if (!mounted) {
    return (
      <nav className={cn(
        "sticky top-0 z-40 transition-all duration-300",
        isHomePage
          ? "bg-white/10 backdrop-blur-md border-b border-white/10"
          : "bg-white shadow-sm border-b"
      )}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link href="/" className="flex items-center rtl:space-x-reverse space-x-2">
                <img src={isHomePage ? (isScrolled ? "/visual-identity/logo-icon.png" : "/visual-identity/logo-icon-night.png") : "/visual-identity/logo-icon.png"} alt="Skill Fits Logo" className="w-8 h-8" />
                <span className="font-bold text-xl">
                  <span style={{ color: isHomePage ? '#ffffff' : '#1a237e' }}>SKILL</span>
                  <span style={{ color: isHomePage ? '#ffffff' : '#00bfa6' }}>FITS</span>
                </span>
              </Link>
            </div>
            <div className="flex items-center">
              <button className={cn(
                "p-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300",
                isHomePage
                  ? "text-white/80 hover:text-white hover:bg-white/10 focus:ring-white"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:ring-blue-500"
              )}>
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>
    )
  }

  const menuItems = [
    {
      title: isRTL ? "الرئيسية" : "Home",
      url: "/",
      icon: Home,
      section: "general"
    },
    ...((!user || !isCompany) ? [{
      title: isRTL ? "تصفح الوظائف" : "Browse Jobs",
      url: "/applicant/jobs",
      icon: Search,
      section: "general"
    }] : []),
    {
      title: isRTL ? "تقييم السير الذاتية بالدفعات" : "Batch CVs Evaluation",
      url: "/employer/evaluate-cv",
      icon: FileText,
      section: "company"
    },
    ...((!user || isCompany) ? [{
      title: isRTL ? "الأسعار" : "Pricing",
      url: "/employer/pricing",
      icon: DollarSign,
      section: "general"
    }] : []),
    {
      title: isRTL ? "لوحة التحكم" : "Dashboard",
      url: "/employer/dashboard",
      icon: LayoutDashboard,
      section: "company"
    },
    {
      title: isRTL ? "نشر وظيفة" : "Post a Job",
      url: "/employer/post-job",
      icon: Briefcase,
      section: "company"
    },
    {
      title: isRTL ? "قائمة الوظائف" : "Job List",
      url: "/employer/jobs",
      icon: Users,
      section: "company"
    },
    {
      title: isRTL ? "الإعدادات" : "Settings",
      url: isCompany ? "/employer/settings" : "/applicant/profile",
      icon: Settings,
      section: "account"
    }
  ]

  return (
    <>
      <nav className={cn(
        "sticky top-0 z-40 transition-all duration-300",
        isHomePage ? (
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-white/20"
            : "bg-white/10 backdrop-blur-md border-b border-white/10"
        ) : (
          "bg-white shadow-sm border-b"
        )
      )}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link href="/" className="flex items-center rtl:space-x-reverse space-x-2">
                <img src={isHomePage ? (isScrolled ? "/visual-identity/logo-icon.png" : "/visual-identity/logo-icon-night.png") : "/visual-identity/logo-icon.png"} alt="Skill Fits Logo" className="w-8 h-8" />
                <span className="font-bold text-xl">
                  <span style={{ color: isHomePage ? (isScrolled ? '#1a237e' : '#ffffff') : '#1a237e' }}>SKILL</span>
                  <span style={{ color: isHomePage ? (isScrolled ? '#00bfa6' : '#ffffff') : '#00bfa6' }}>FITS</span>
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center rtl:space-x-reverse space-x-6">
              {(!user || isCompany) && (
                <Link
                  href="/employer/pricing"
                  className={cn(
                    "px-3 py-2 rounded-md text-sm font-medium transition-all duration-300",
                    isHomePage ? (
                      isScrolled
                        ? "text-gray-700 hover:text-midnight-blue hover:bg-midnight-blue/5"
                        : "text-white/90 hover:text-white hover:bg-white/10"
                    ) : (
                      "text-gray-700 hover:text-midnight-blue hover:bg-midnight-blue/5"
                    ),
                    isActive("/employer/pricing") && (isHomePage && !isScrolled ? "text-white bg-white/20" : "text-midnight-blue bg-midnight-blue/10")
                  )}
                >
                  {isRTL ? "الأسعار" : "Pricing"}
                </Link>
              )}
              {(!user || !isCompany) && (
                <Link
                  href="/applicant/jobs"
                  className={cn(
                    "px-3 py-2 rounded-md text-sm font-medium transition-all duration-300",
                    isHomePage ? (
                      isScrolled
                        ? "text-gray-700 hover:text-midnight-blue hover:bg-midnight-blue/5"
                        : "text-white/90 hover:text-white hover:bg-white/10"
                    ) : (
                      "text-gray-700 hover:text-midnight-blue hover:bg-midnight-blue/5"
                    ),
                    isActive("/applicant/jobs") && (isHomePage && !isScrolled ? "text-white bg-white/20" : "text-midnight-blue bg-midnight-blue/10")
                  )}
                >
                  {isRTL ? "تصفح الوظائف" : "Browse Jobs"}
                </Link>
              )}
              {user && isCompany && (
                <Link
                  href="/employer/evaluate-cv"
                  className={cn(
                    "px-3 py-2 rounded-md text-sm font-medium transition-all duration-300",
                    isHomePage ? (
                      isScrolled
                        ? "text-gray-700 hover:text-midnight-blue hover:bg-midnight-blue/5"
                        : "text-white/90 hover:text-white hover:bg-white/10"
                    ) : (
                      "text-gray-700 hover:text-midnight-blue hover:bg-midnight-blue/5"
                    ),
                    isActive("/employer/evaluate-cv") && (isHomePage && !isScrolled ? "text-white bg-white/20" : "text-midnight-blue bg-midnight-blue/10")
                  )}
                >
                  {isRTL ? "تقييم السير الذاتية بالدفعات" : "Batch CVs Evaluation"}
                </Link>
              )}

              {/* Language Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "flex items-center rtl:space-x-reverse space-x-2 transition-all duration-300",
                      isHomePage ? (
                        isScrolled
                          ? "bg-white/80 border-gray-200 text-gray-700 hover:bg-white"
                          : "bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
                      ) : (
                        "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                      )
                    )}
                  >
                    <Globe className="w-4 h-4" />
                    <span>{i18n.language.toUpperCase()}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => changeLanguage("en")}>English (EN)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLanguage("ar")}>العربية (AR)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Navigation */}
              {!loading && (
                <div className="flex items-center rtl:space-x-reverse space-x-3">
                  {user ? (
                    <Link href={isCompany ? "/employer/dashboard" : "/applicant/dashboard"}>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "flex items-center rtl:space-x-reverse space-x-2 transition-all duration-300",
                          isHomePage ? (
                            isScrolled
                              ? "bg-white/80 border-gray-200 text-gray-700 hover:bg-white"
                              : "bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
                          ) : (
                            "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                          )
                        )}
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        <span>{isCompany ? (isRTL ? "لوحة التحكم" : "Dashboard") : (isRTL ? "ملفي الشخصي" : "My Profile")}</span>
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link href="/auth/applicant/login">
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            "flex items-center rtl:space-x-reverse space-x-2 transition-all duration-300",
                            isHomePage ? (
                              isScrolled
                                ? "bg-white/80 border-gray-200 text-gray-700 hover:bg-white"
                                : "bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
                            ) : (
                              "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                            )
                          )}
                        >
                          <User className="w-4 h-4" />
                          <span>{isRTL ? "متقدم" : "Applicant"}</span>
                        </Button>
                      </Link>
                      <Link href="/auth/employer/login">
                        <Button
                          size="sm"
                          className={cn(
                            "flex items-center rtl:space-x-reverse space-x-2 transition-all duration-300",
                            isHomePage ? (
                              isScrolled
                                ? "bg-midnight-blue hover:bg-midnight-blue/90 text-white"
                                : "bg-gradient-to-r from-midnight-blue to-electric-teal hover:from-midnight-blue/90 hover:to-electric-teal/90 text-white"
                            ) : (
                              "bg-midnight-blue hover:bg-midnight-blue/90 text-white"
                            )
                          )}
                        >
                          <Building2 className="w-4 h-4" />
                          <span>{isRTL ? "صاحب عمل" : "Employer"}</span>
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Controls */}
            <div className="flex items-center rtl:space-x-reverse space-x-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "md:hidden transition-all duration-300",
                      isScrolled
                        ? "text-gray-700 hover:bg-gray-100"
                        : "text-white hover:bg-white/10"
                    )}
                  >
                    <Globe className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => changeLanguage("en")}>English (EN)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLanguage("ar")}>العربية (AR)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <button
                onClick={toggleMenu}
                className={cn(
                  "p-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300",
                  isHomePage ? (
                    isScrolled
                      ? "text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:ring-blue-500"
                      : "text-white/80 hover:text-white hover:bg-white/10 focus:ring-white"
                  ) : (
                    "text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:ring-blue-500"
                  )
                )}
                aria-label="Toggle menu"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      {mounted && isOpen && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 9999 }}>
          {/* Background Overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(8px)',
              zIndex: 9998
            }}

            onClick={() => setIsOpen(false)}
          />

          {/* Sidebar Panel */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              [isRTL ? 'left' : 'right']: 0,
              height: '100vh',
              width: '320px',
              backgroundColor: 'white',
              boxShadow: isRTL
                ? '4px 0 25px -5px rgba(0, 0, 0, 0.1), 10px 0 10px -5px rgba(0, 0, 0, 0.04)'
                : '-4px 0 25px -5px rgba(0, 0, 0, 0.1), -10px 0 10px -5px rgba(0, 0, 0, 0.04)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              transform: 'translateX(0)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 10000
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: isHomePage ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #f1f5f9',
                backgroundColor: isHomePage ? 'rgba(255, 255, 255, 0.1)' : '#fafafa',
                backdropFilter: isHomePage ? 'blur(10px)' : 'none'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexDirection: isRTL ? 'row-reverse' : 'row'
              }}>
                <img src={isHomePage ? (isScrolled ? "/visual-identity/logo-icon.png" : "/visual-identity/logo-icon-night.png") : "/visual-identity/logo-icon.png"} alt="Skill Fits Logo" style={{ width: '32px', height: '32px', display: 'block' }} />
                <span style={{
                  fontWeight: '600',
                  fontSize: '18px',
                  direction: isRTL ? 'rtl' : 'ltr',
                  fontFamily: 'Arial, sans-serif'
                }}>
                  <span style={{ color: '#1a237e' }}>SKILL</span>
                  <span style={{ color: '#00bfa6' }}>FITS</span>
                </span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  padding: '8px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9'
                  e.currentTarget.style.color = '#334155'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = '#64748b'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* User Info */}
            {user && (
              <div
                style={{
                  padding: '20px',
                  borderBottom: '1px solid #f1f5f9',
                  backgroundColor: '#f8fafc'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexDirection: isRTL ? 'row-reverse' : 'row'
                }}>
                  <Avatar className="h-12 w-12">
                    <AvatarImage
                      src={isCompany ? company?.logo_url || undefined : undefined}
                      alt={isCompany ? company?.company_name || "Company" : "User"}
                    />
                    <AvatarFallback>
                      {isCompany ? <Building2 className="h-6 w-6" /> : <User className="h-6 w-6" />}
                    </AvatarFallback>
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0, textAlign: isRTL ? 'right' : 'left' }}>
                    <p style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1e293b',
                      margin: '0 0 4px 0',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}>
                      {isCompany ? company?.company_name || (isRTL ? "صاحب العمل" : "Employer") : (isRTL ? "متقدم" : "Applicant")}
                    </p>
                    <p style={{
                      fontSize: '14px',
                      color: '#64748b',
                      margin: 0,
                      direction: 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Menu Content */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '24px 20px'
              }}
            >
              {/* General Navigation */}
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  marginBottom: '16px',
                  margin: '0 0 16px 0',
                  textAlign: isRTL ? 'right' : 'left',
                  direction: isRTL ? 'rtl' : 'ltr'
                }}>
                  {isRTL ? "التنقل العام" : "General Navigation"}
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {menuItems
                    .filter(item => item.section === "general")
                    .map((item, index) => {
                      const IconComponent = item.icon
                      const isItemActive = isActive(item.url)
                      return (
                        <Link
                          key={`general-${index}`}
                          href={item.url}
                          onClick={() => setIsOpen(false)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            textDecoration: 'none',
                            fontSize: '15px',
                            fontWeight: '500',
                            backgroundColor: isItemActive ? '#eff6ff' : 'transparent',
                            color: isItemActive ? '#1d4ed8' : '#475569',
                            border: isItemActive ? '1px solid #93c5fd' : '1px solid transparent',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            gap: '12px',
                            flexDirection: isRTL ? 'row-reverse' : 'row',
                            textAlign: isRTL ? 'right' : 'left',
                            direction: isRTL ? 'rtl' : 'ltr'
                          }}
                          onMouseEnter={(e) => {
                            if (!isItemActive) {
                              e.currentTarget.style.backgroundColor = '#f8fafc'
                              e.currentTarget.style.color = '#334155'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isItemActive) {
                              e.currentTarget.style.backgroundColor = 'transparent'
                              e.currentTarget.style.color = '#475569'
                            }
                          }}
                        >
                          <IconComponent
                            size={20}
                            style={{
                              color: isItemActive ? '#1d4ed8' : '#64748b',
                              flexShrink: 0
                            }}
                          />
                          <span style={{ flex: 1 }}>{item.title}</span>
                        </Link>
                      )
                    })}
                </div>
              </div>

              {/* Company Tools */}
              {user && isCompany && (
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '16px',
                    margin: '0 0 16px 0',
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}>
                    {isRTL ? "أدوات الشركة" : "Company Tools"}
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {menuItems
                      .filter(item => item.section === "company")
                      .map((item, index) => {
                        const IconComponent = item.icon
                        const isItemActive = isActive(item.url)
                        return (
                          <Link
                            key={`company-${index}`}
                            href={item.url}
                            onClick={() => setIsOpen(false)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              textDecoration: 'none',
                              fontSize: '15px',
                              fontWeight: '500',
                              backgroundColor: isItemActive ? '#f0fdf4' : 'transparent',
                              color: isItemActive ? '#166534' : '#475569',
                              border: isItemActive ? '1px solid #bbf7d0' : '1px solid transparent',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              gap: '12px',
                              flexDirection: isRTL ? 'row-reverse' : 'row',
                              textAlign: isRTL ? 'right' : 'left',
                              direction: isRTL ? 'rtl' : 'ltr'
                            }}
                            onMouseEnter={(e) => {
                              if (!isItemActive) {
                                e.currentTarget.style.backgroundColor = '#f8fafc'
                                e.currentTarget.style.color = '#334155'
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isItemActive) {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#475569'
                              }
                            }}
                          >
                            <IconComponent
                              size={20}
                              style={{
                                color: isItemActive ? '#166534' : '#64748b',
                                flexShrink: 0
                              }}
                            />
                            <span style={{ flex: 1 }}>{item.title}</span>
                          </Link>
                        )
                      })}
                  </div>
                </div>
              )}

              {/* Account Section */}
              {user && (
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '16px',
                    margin: '0 0 16px 0',
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}>
                    {isRTL ? "قسم الحساب" : "Account"}
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {menuItems
                      .filter(item => item.section === "account")
                      .map((item, index) => {
                        const IconComponent = item.icon
                        const isItemActive = isActive(item.url)
                        return (
                          <Link
                            key={`account-${index}`}
                            href={item.url}
                            onClick={() => setIsOpen(false)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              textDecoration: 'none',
                              fontSize: '15px',
                              fontWeight: '500',
                              backgroundColor: isItemActive ? '#faf5ff' : 'transparent',
                              color: isItemActive ? '#7c3aed' : '#475569',
                              border: isItemActive ? '1px solid #d8b4fe' : '1px solid transparent',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              gap: '12px',
                              flexDirection: isRTL ? 'row-reverse' : 'row',
                              textAlign: isRTL ? 'right' : 'left',
                              direction: isRTL ? 'rtl' : 'ltr'
                            }}
                            onMouseEnter={(e) => {
                              if (!isItemActive) {
                                e.currentTarget.style.backgroundColor = '#f8fafc'
                                e.currentTarget.style.color = '#334155'
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isItemActive) {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#475569'
                              }
                            }}
                          >
                            <IconComponent
                              size={20}
                              style={{
                                color: isItemActive ? '#7c3aed' : '#64748b',
                                flexShrink: 0
                              }}
                            />
                            <span style={{ flex: 1 }}>{item.title}</span>
                          </Link>
                        )
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer - Logout/Login */}
            <div style={{
              borderTop: '1px solid #f1f5f9',
              padding: '20px',
              backgroundColor: '#fafafa'
            }}>
              {user ? (
                <button
                  onClick={handleLogout}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: '1px solid #fee2e2',
                    backgroundColor: '#fef2f2',
                    color: '#dc2626',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    gap: '12px',
                    flexDirection: isRTL ? 'row-reverse' : 'row',
                    textAlign: isRTL ? 'right' : 'left',
                    direction: isRTL ? 'rtl' : 'ltr'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee2e2'
                    e.currentTarget.style.borderColor = '#fca5a5'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fef2f2'
                    e.currentTarget.style.borderColor = '#fee2e2'
                  }}
                >
                  <LogOut size={20} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>
                    {isRTL ? "تسجيل الخروج" : "Sign Out"}
                  </span>
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Link
                    href="/auth/applicant/login"
                    onClick={() => setIsOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: 'white',
                      color: '#475569',
                      fontSize: '15px',
                      fontWeight: '500',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      gap: '12px',
                      flexDirection: isRTL ? 'row-reverse' : 'row',
                      textAlign: isRTL ? 'right' : 'left',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc'
                      e.currentTarget.style.borderColor = '#cbd5e1'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white'
                      e.currentTarget.style.borderColor = '#e2e8f0'
                    }}
                  >
                    <User size={20} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      {isRTL ? "تسجيل دخول المتقدم" : "Applicant Login"}
                    </span>
                  </Link>
                  <Link
                    href="/auth/employer/login"
                    onClick={() => setIsOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: 'none',
                      background: 'linear-gradient(to right, #1a237e, #00bfa6)',
                      color: 'white',
                      fontSize: '15px',
                      fontWeight: '500',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      gap: '12px',
                      flexDirection: isRTL ? 'row-reverse' : 'row',
                      textAlign: isRTL ? 'right' : 'left',
                      direction: isRTL ? 'rtl' : 'ltr'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(to right, #151e6a, #00a08d)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(to right, #1a237e, #00bfa6)'
                    }}
                  >
                    <Building2 size={20} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      {isRTL ? "تسجيل دخول صاحب عمل" : "Employer Login"}
                    </span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Navigation
